import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLeads } from '@/hooks/useLeads';
import { supabase } from '@/lib/supabase';
import { Search, Phone, Mail, MessageCircle, ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { nl } from 'date-fns/locale';

function statusPillStyle(status: string) {
  if (['afgerond', 'nabetaling_ontvangen', 'getekend', 'akkoord'].includes(status))
    return { bg: 'var(--success-bg)', color: 'var(--success-text)', border: 'var(--success-border)' };
  if (status === 'new') return { bg: 'var(--warning-bg)', color: 'var(--warning-text)', border: 'var(--warning-border)' };
  if (status === 'verloren') return { bg: 'var(--danger-bg)', color: 'var(--danger-text)', border: 'var(--danger-border)' };
  return { bg: 'var(--accent-dim)', color: 'var(--accent)', border: 'var(--accent)' };
}

const STAGE_LABELS: Record<string, string> = {
  new: 'Nieuw', contacted: 'Gecontacteerd', showroom_planned: 'Showroom gepland',
  showroom_visited: 'Showroom bezocht', offerte_verstuurd: 'Offerte verstuurd',
  offerte_herzien: 'Offerte herzien', akkoord: 'Akkoord', getekend: 'Getekend',
  aanbetaling_gevraagd: 'Aanbetaling gevraagd', aanbetaling_ontvangen: 'Aanbetaling ontvangen',
  werkbezoek_gepland: 'Werkbezoek gepland', werkbezoek_gedaan: 'Werkbezoek gedaan',
  materiaal_besteld: 'Materiaal besteld', levering_gepland: 'Levering gepland',
  geleverd: 'Geleverd', afgerond: 'Afgerond', verloren: 'Verloren',
};

type SourceType = 'facebook' | 'website' | 'semmy' | 'google' | 'other';

function detectSource(raw: Record<string, unknown>): { label: string; type: SourceType } {
  const src = ((raw.bron as string) || (raw.source as string) || '').toLowerCase();
  const utm = ((raw.utm_source as string) || '').toLowerCase();
  const ref = ((raw.referrer as string) || '').toLowerCase();

  if (src.includes('facebook') || src.includes('fb') || raw.created_time || raw.ad_id || raw.form_id) {
    return { label: 'Facebook', type: 'facebook' };
  }
  if (utm.includes('facebook') || utm.includes('fb') || ref.includes('facebook')) {
    return { label: 'Facebook', type: 'facebook' };
  }
  if (src === 'manual') {
    return { label: 'Handmatig', type: 'other' };
  }
  if (src.includes('semmy') || utm.includes('semmy')) {
    return { label: 'Semmy', type: 'semmy' };
  }
  if (utm.includes('google') || ref.includes('google')) {
    return { label: 'Google', type: 'google' };
  }
  return { label: 'Website', type: 'website' };
}

const SOURCE_COLORS: Record<SourceType, { bg: string; color: string; border: string }> = {
  facebook: { bg: 'rgba(24,119,242,0.1)', color: '#1877F2', border: 'rgba(24,119,242,0.25)' },
  google:   { bg: 'rgba(234,67,53,0.1)',  color: '#EA4335', border: 'rgba(234,67,53,0.25)' },
  semmy:    { bg: 'var(--accent-dim)',     color: 'var(--accent)', border: 'var(--accent)' },
  website:  { bg: 'var(--bg-input)',       color: 'var(--text-tertiary)', border: 'var(--border-primary)' },
  other:    { bg: 'var(--bg-input)',       color: 'var(--text-tertiary)', border: 'var(--border-primary)' },
};

type SortKey = 'name' | 'status' | 'source' | 'created_at' | 'estimated';
type SortDir = 'asc' | 'desc';

const PAGE_SIZES = [10, 50, 100];

const VLOERTYPES = ['Visgraat', 'Betonlook', 'Hout / Plank', 'Tegel', 'Gietvloer', 'Hongaarse punt'];

const FIELD_STYLE: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: '10px',
  border: '1px solid var(--border-primary)',
  background: 'var(--bg-input)',
  color: 'var(--text-primary)',
  fontSize: '13px',
  outline: 'none',
};

const LABEL_STYLE: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  marginBottom: '4px',
  display: 'block',
};

interface AddLeadForm {
  voornaam: string;
  achternaam: string;
  email: string;
  telefoon: string;
  vloertype: string;
  oppervlakte: string;
  postcode: string;
  stad: string;
}

const EMPTY_FORM: AddLeadForm = {
  voornaam: '', achternaam: '', email: '', telefoon: '',
  vloertype: '', oppervlakte: '', postcode: '', stad: '',
};

function AddLeadModal({
  partnerId,
  onClose,
  onSuccess,
}: {
  partnerId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [form, setForm] = useState<AddLeadForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  function set(field: keyof AddLeadForm, value: string) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.voornaam.trim() || !form.email.trim() || !form.telefoon.trim()) return;
    setSubmitting(true);
    setErr(null);
    try {
      // Insert lead row
      const { data: newLead, error: insertErr } = await supabase
        .from('leads')
        .insert({
          assigned_partner_id: partnerId,
          status: 'submitted',
          raw_payload: {
            voornaam:    form.voornaam.trim(),
            achternaam:  form.achternaam.trim() || null,
            email:       form.email.trim(),
            telefoon:    form.telefoon.trim(),
            vloertype:   form.vloertype || null,
            oppervlakte: form.oppervlakte || null,
            postcode:    form.postcode.trim() || null,
            stad:        form.stad.trim() || null,
            bron:        'manual',
          },
        })
        .select('id')
        .single();

      if (insertErr || !newLead) throw new Error(insertErr?.message || 'Lead aanmaken mislukt');

      // Trigger full intake flow
      const { error: fnErr } = await supabase.functions.invoke('semmy-lead-intake', {
        body: { lead_id: newLead.id, partner_id: partnerId },
      });
      if (fnErr) console.warn('semmy-lead-intake warn:', fnErr.message);

      setDone(true);
      setTimeout(() => { onSuccess(); onClose(); }, 1400);
    } catch (e: any) {
      setErr(e.message || 'Er is iets misgegaan.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      ref={overlayRef}
      onClick={e => { if (e.target === overlayRef.current) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div style={{
        width: '100%', maxWidth: '480px',
        background: 'var(--bg-surface-raised)',
        border: '1px solid var(--border-separator)',
        borderRadius: '20px',
        boxShadow: '0 24px 80px rgba(0,0,0,0.35)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 20px 16px',
          borderBottom: '1px solid var(--border-separator)',
        }}>
          <div>
            <p style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Lead toevoegen</p>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '2px 0 0' }}>Semmy stuurt direct een WhatsApp en welkomstmail</p>
          </div>
          <button onClick={onClose} className="btn-icon" style={{ width: 30, height: 30, flexShrink: 0 }}>
            <X size={14} />
          </button>
        </div>

        {done ? (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: '28px', marginBottom: '10px' }}>✓</div>
            <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Lead aangemaakt</p>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '4px 0 0' }}>Semmy neemt contact op</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={LABEL_STYLE}>Voornaam *</label>
                <input style={FIELD_STYLE} value={form.voornaam} onChange={e => set('voornaam', e.target.value)} required autoFocus />
              </div>
              <div>
                <label style={LABEL_STYLE}>Achternaam</label>
                <input style={FIELD_STYLE} value={form.achternaam} onChange={e => set('achternaam', e.target.value)} />
              </div>
            </div>

            <div style={{ marginTop: '12px' }}>
              <label style={LABEL_STYLE}>E-mailadres *</label>
              <input type="email" style={FIELD_STYLE} value={form.email} onChange={e => set('email', e.target.value)} required />
            </div>

            <div style={{ marginTop: '12px' }}>
              <label style={LABEL_STYLE}>Telefoonnummer *</label>
              <input type="tel" style={FIELD_STYLE} placeholder="+31 6 12345678" value={form.telefoon} onChange={e => set('telefoon', e.target.value)} required />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
              <div>
                <label style={LABEL_STYLE}>Vloertype</label>
                <select style={{ ...FIELD_STYLE, appearance: 'none' }} value={form.vloertype} onChange={e => set('vloertype', e.target.value)}>
                  <option value="">— kies —</option>
                  {VLOERTYPES.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label style={LABEL_STYLE}>Oppervlakte (m²)</label>
                <input type="number" min="1" step="1" style={FIELD_STYLE} placeholder="bijv. 45" value={form.oppervlakte} onChange={e => set('oppervlakte', e.target.value)} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
              <div>
                <label style={LABEL_STYLE}>Postcode</label>
                <input style={FIELD_STYLE} placeholder="1234 AB" value={form.postcode} onChange={e => set('postcode', e.target.value)} />
              </div>
              <div>
                <label style={LABEL_STYLE}>Stad</label>
                <input style={FIELD_STYLE} placeholder="Amsterdam" value={form.stad} onChange={e => set('stad', e.target.value)} />
              </div>
            </div>

            {err && (
              <p style={{ fontSize: '12px', color: 'var(--danger-text)', marginTop: '12px', background: 'var(--danger-bg)', border: '1px solid var(--danger-border)', borderRadius: '8px', padding: '8px 12px' }}>
                {err}
              </p>
            )}

            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  flex: 1, padding: '10px', borderRadius: '10px',
                  border: '1px solid var(--border-primary)',
                  background: 'var(--bg-input)', color: 'var(--text-secondary)',
                  fontSize: '13px', fontWeight: 500, cursor: 'pointer',
                }}
              >
                Annuleren
              </button>
              <button
                type="submit"
                disabled={submitting || !form.voornaam.trim() || !form.email.trim() || !form.telefoon.trim()}
                style={{
                  flex: 2, padding: '10px', borderRadius: '10px',
                  border: 'none',
                  background: submitting ? 'var(--accent-dim)' : 'var(--accent)',
                  color: submitting ? 'var(--accent)' : '#fff',
                  fontSize: '13px', fontWeight: 600, cursor: submitting ? 'default' : 'pointer',
                  opacity: (!form.voornaam.trim() || !form.email.trim() || !form.telefoon.trim()) ? 0.45 : 1,
                  transition: 'opacity 0.15s',
                }}
              >
                {submitting ? 'Bezig...' : 'Lead aanmaken & flow starten'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const TH_STYLE: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'left',
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  whiteSpace: 'nowrap',
  userSelect: 'none',
};

export default function PartnerLeadsPage() {
  const navigate = useNavigate();
  const { effectivePartnerId } = useAuth();
  const { leads: liveLeads, loading: leadsLoading, refetch } = useLeads(effectivePartnerId);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [showAddModal, setShowAddModal] = useState(false);

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
    setPage(0);
  }

  const filtered = useMemo(() => {
    return liveLeads.filter(l =>
      !searchQuery ||
      `${l.firstname} ${l.lastname}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      l.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      l.postcode.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [liveLeads, searchQuery]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') {
        cmp = `${a.firstname} ${a.lastname}`.localeCompare(`${b.firstname} ${b.lastname}`, 'nl');
      } else if (sortKey === 'status') {
        cmp = (STAGE_LABELS[a.status] || a.status).localeCompare(STAGE_LABELS[b.status] || b.status, 'nl');
      } else if (sortKey === 'created_at') {
        cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      } else if (sortKey === 'estimated') {
        cmp = (a.estimated_min_eur ?? 0) - (b.estimated_min_eur ?? 0);
      } else if (sortKey === 'source') {
        cmp = detectSource(a.raw_payload).label.localeCompare(detectSource(b.raw_payload).label, 'nl');
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages - 1);
  const paginated = sorted.slice(currentPage * pageSize, currentPage * pageSize + pageSize);

  function SortIcon({ col }: { col: SortKey }) {
    if (sortKey !== col) return <ChevronsUpDown size={11} style={{ opacity: 0.35, flexShrink: 0 }} />;
    return sortDir === 'asc'
      ? <ChevronUp size={11} style={{ color: 'var(--accent)', flexShrink: 0 }} />
      : <ChevronDown size={11} style={{ color: 'var(--accent)', flexShrink: 0 }} />;
  }

  function SortableTH({ col, children }: { col: SortKey; children: React.ReactNode }) {
    return (
      <th
        onClick={() => handleSort(col)}
        style={{ ...TH_STYLE, cursor: 'pointer' }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          {children}
          <SortIcon col={col} />
        </span>
      </th>
    );
  }

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-end justify-between stagger-1">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Mijn leads</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{filtered.length} leads</p>
        </div>
        {effectivePartnerId && (
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '8px 14px', borderRadius: '10px',
              background: 'var(--accent)', color: '#fff',
              border: 'none', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <Plus size={14} />
            Lead toevoegen
          </button>
        )}
      </div>

      {showAddModal && effectivePartnerId && (
        <AddLeadModal
          partnerId={effectivePartnerId}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => refetch()}
        />
      )}

      <div className="stagger-2">
        <div className="aura-input-wrap" style={{ borderRadius: '14px' }}>
          <div className="aura-input-inner" style={{ borderRadius: '13px', paddingLeft: '16px', paddingRight: '16px' }}>
            <Search size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <input
              type="text"
              placeholder="Zoek op naam, e-mail, postcode..."
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setPage(0); }}
              className="flex-1 py-2.5 text-sm outline-none bg-transparent"
              style={{ color: 'var(--text-primary)', minWidth: 0 }}
            />
          </div>
        </div>
      </div>

      {leadsLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 rounded-full border-2 animate-spin" style={{ borderColor: 'var(--border-primary)', borderTopColor: 'var(--accent)' }} />
        </div>
      )}

      {!leadsLoading && sorted.length > 0 && (
        <div className="stagger-3" style={{ borderRadius: '14px', border: '1px solid var(--border-separator)', overflow: 'hidden', background: 'var(--bg-surface-raised)' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-separator)' }}>
                  <SortableTH col="name">Naam</SortableTH>
                  <SortableTH col="status">Status</SortableTH>
                  <SortableTH col="source">Bron</SortableTH>
                  <th style={TH_STYLE}>Vloertype</th>
                  <th style={TH_STYLE}>m²</th>
                  <th style={TH_STYLE}>Locatie</th>
                  <SortableTH col="created_at">Aangemeld</SortableTH>
                  <SortableTH col="estimated">Waarde</SortableTH>
                  <th style={{ ...TH_STYLE, textAlign: 'right' }}>Contact</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((lead, i) => {
                  const raw = lead.raw_payload;
                  const vloer = String(lead.intake_data?.floor_type || '');
                  const m2 = String(lead.intake_data?.surface_area_m2 || '');
                  const locatie = [lead.postcode, lead.city].filter(Boolean).join(' ');
                  const pill = statusPillStyle(lead.status);
                  const statusLabel = STAGE_LABELS[lead.status] || lead.status.replace(/_/g, ' ');
                  const source = detectSource(raw);
                  const srcColor = SOURCE_COLORS[source.type];
                  const isLast = i === paginated.length - 1;
                  const waPhone = (lead.phone || '').replace(/[\s\-\(\)]/g, '').replace(/^0/, '+31').replace(/^\+/, '');

                  return (
                    <tr
                      key={lead.id}
                      onClick={() => navigate(`/partner/leads/${lead.id}`)}
                      className="leads-table-row"
                      style={{
                        cursor: 'pointer',
                        borderBottom: isLast ? 'none' : '1px solid var(--border-separator)',
                      }}
                    >
                      <td style={{ padding: '11px 14px', fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                        {lead.firstname} {lead.lastname}
                      </td>

                      <td style={{ padding: '11px 14px' }}>
                        <span style={{
                          fontSize: '11px', padding: '2px 8px', borderRadius: '999px',
                          background: pill.bg, color: pill.color, border: `1px solid ${pill.border}`,
                          whiteSpace: 'nowrap',
                        }}>
                          {statusLabel}
                        </span>
                      </td>

                      <td style={{ padding: '11px 14px' }}>
                        <span style={{
                          fontSize: '11px', padding: '2px 8px', borderRadius: '999px',
                          background: srcColor.bg, color: srcColor.color, border: `1px solid ${srcColor.border}`,
                          whiteSpace: 'nowrap',
                        }}>
                          {source.label}
                        </span>
                      </td>

                      <td style={{ padding: '11px 14px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {vloer || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>

                      <td style={{ padding: '11px 14px', color: 'var(--text-secondary)' }}>
                        {m2 ? `${m2} m²` : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>

                      <td style={{ padding: '11px 14px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {locatie || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>

                      <td style={{ padding: '11px 14px', color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
                        {formatDistanceToNow(
                          new Date(lead.created_at),
                          { addSuffix: true, locale: nl }
                        )}
                      </td>

                      <td style={{ padding: '11px 14px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {lead.estimated_min_eur
                          ? `€${lead.estimated_min_eur.toLocaleString('nl-NL')}${lead.estimated_max_eur ? `–${lead.estimated_max_eur.toLocaleString('nl-NL')}` : '+'}`
                          : <span style={{ color: 'var(--text-muted)' }}>—</span>
                        }
                      </td>

                      <td style={{ padding: '11px 14px' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
                          {lead.phone && (
                            <a href={`tel:${lead.phone}`} className="btn-icon" title="Bellen" style={{ width: 28, height: 28, fontSize: 13 }}>
                              <Phone size={13} />
                            </a>
                          )}
                          {lead.phone && (
                            <a
                              href={`https://wa.me/${waPhone}`}
                              target="_blank" rel="noreferrer"
                              className="btn-icon" title="WhatsApp"
                              style={{ width: 28, height: 28, fontSize: 13 }}
                            >
                              <MessageCircle size={13} />
                            </a>
                          )}
                          {lead.email && (
                            <a href={`mailto:${lead.email}`} className="btn-icon" title="E-mail" style={{ width: 28, height: 28, fontSize: 13 }}>
                              <Mail size={13} />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px',
            padding: '11px 14px', borderTop: '1px solid var(--border-separator)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
              <span>Toon</span>
              {PAGE_SIZES.map(sz => (
                <button
                  key={sz}
                  onClick={() => { setPageSize(sz); setPage(0); }}
                  style={{
                    padding: '3px 9px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer',
                    background: pageSize === sz ? 'var(--text-primary)' : 'var(--bg-input)',
                    color: pageSize === sz ? 'var(--bg-surface)' : 'var(--text-secondary)',
                    border: `1px solid ${pageSize === sz ? 'transparent' : 'var(--border-separator)'}`,
                  }}
                >
                  {sz}
                </button>
              ))}
              <span>per pagina · {filtered.length} totaal</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                {filtered.length === 0 ? '0' : currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, filtered.length)} van {filtered.length}
              </span>
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                className="btn-icon"
                style={{ width: 28, height: 28, opacity: currentPage === 0 ? 0.35 : 1 }}
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={currentPage >= totalPages - 1}
                className="btn-icon"
                style={{ width: 28, height: 28, opacity: currentPage >= totalPages - 1 ? 0.35 : 1 }}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {filtered.length === 0 && !leadsLoading && (
        <div className="text-center py-16">
          <p className="text-base font-medium" style={{ color: 'var(--text-tertiary)' }}>
            {searchQuery ? 'Geen leads gevonden' : 'Geen leads toegewezen'}
          </p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            {searchQuery ? 'Probeer een andere zoekopdracht.' : 'Leads verschijnen hier zodra ze aan u zijn toegewezen.'}
          </p>
        </div>
      )}
    </div>
  );
}
